import { Alert, AlertDialog } from '@ntconsult/aluma-react';
import { useTranslation } from 'react-i18next';
import type { AcaoLote } from '../types/Painel';

type Props = {
  acao: AcaoLote | null;
  quantidade: number;
  /** Quantas já responderam; `null` quando não dá para saber (seleção "todos do filtro" com filtros). */
  comResposta: number | null;
  /** Nomes da seleção por ids, para ninguém sair sem aparecer na tela (spec 25 R5); vazio no "todos do filtro". */
  nomes: readonly string[];
  pending: boolean;
  onConfirmar: () => void;
  onCancelar: () => void;
};

const MAX_NOMES = 50;

/** Até 50 nomes e "e mais K" (spec 25 R5). */
export function ListaNomes({ nomes, max = MAX_NOMES }: { nomes: readonly string[]; max?: number }) {
  const { t } = useTranslation('participantes');
  if (nomes.length === 0) return null;
  const resto = nomes.length - max;
  return (
    <ul className="max-h-48 overflow-y-auto text-sm text-foreground">
      {nomes.slice(0, max).map((n, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: nomes podem repetir
        <li key={`${n}-${i}`}>{n}</li>
      ))}
      {resto > 0 && <li className="text-foreground-muted">{t('lote.eMais', { count: resto })}</li>}
    </ul>
  );
}

/** Confirmação antes de excluir ou devolver: diz quantas pessoas e o que acontece (spec 14 R6). */
export function ConfirmarLoteDialog({
  acao,
  quantidade,
  comResposta,
  nomes,
  pending,
  onConfirmar,
  onCancelar,
}: Props) {
  const { t } = useTranslation(['participantes', 'common']);
  const excluir = acao === 'excluir';

  return (
    <AlertDialog
      open={acao !== null}
      onOpenChange={(aberto) => {
        if (!aberto) {
          onCancelar();
        }
      }}
    >
      <AlertDialog.Content>
        <AlertDialog.Title>
          {t(excluir ? 'lote.excluirTitulo' : 'lote.devolverTitulo', { count: quantidade })}
        </AlertDialog.Title>
        <AlertDialog.Description>
          {t(excluir ? 'lote.excluirCorpo' : 'lote.devolverCorpo', { count: quantidade })}
        </AlertDialog.Description>
        <ListaNomes nomes={nomes} />
        {excluir && !!comResposta && (
          <Alert variant="warning" role="status">
            {t('lote.comResposta', { count: comResposta })}
          </Alert>
        )}
        <AlertDialog.Footer>
          <AlertDialog.Cancel variant="secondary">{t('common:cancel')}</AlertDialog.Cancel>
          {/* O único primário do diálogo; sem vermelho, excluir se desfaz (spec 32 R5). */}
          <AlertDialog.Action variant="primary" loading={pending} onClick={() => onConfirmar()}>
            {t(excluir ? 'lote.excluir' : 'lote.devolver')}
          </AlertDialog.Action>
        </AlertDialog.Footer>
      </AlertDialog.Content>
    </AlertDialog>
  );
}
